import { apiErrorText } from "../../../api/api-error.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
import { requiredGroupKeyOf, wizardErrorText } from "../new/information-input.js";
/**
 * `data-screen-label="Agent"`, tab `versions` — "Edit" (customer-portal review #75, S1; fix round P).
 *
 * The first real call found it: the owner wanted the agent open 24/7 and there was no way to change
 * anything after the first build, so they built a second agent and moved the number. This panel is
 * the edit. It opens on the Versions tab, pre-filled with the answers of the version it edits (the
 * live one, or another built one with "Edit from here"), and draws them with the NEW-AGENT WIZARD's
 * own controls and rules — the order-book panel and "How callers pay", the fee and threshold boxes
 * with the factory's amount rule, the opening-hours week editor, the language chips, the greeting, a
 * voice for each chosen language — so an edit cannot accept what a build would refuse.
 *
 * WHAT SAVE DOES (`./edit-rules.ts`, `saveActOf`):
 *
 *   · anything the prompt, the menu or the voice depends on changed → `POST /v1/agents/{id}/edit`:
 *     a NEW version is opened and built. With the document kept, the build starts at once; with a
 *     new file, the file is uploaded and then the build is started (the wizard's own upload path).
 *     The live version keeps answering. The panel closes on the new version, which the tab shows as
 *     Building, then Not published — test it, then Publish (calls in progress finish on the version
 *     they started on, design §7.5);
 *   · only "Record calls" changed → that switch alone, published at once, no build (RC-47);
 *   · nothing changed → nothing is sent, and the button says so.
 *
 * A VOICE PER LANGUAGE (plan-015 G1U, gap G1): the voices are Agent setup's own "Voices" rows — one per chosen
 * language, the voices that speak it, the greeting language's Play speaking the greeting as typed (plan-015 GRT) — and
 * Save sends the whole map as `voice_by_language`. Changing any language's voice builds a new version.
 *
 * Refusals come back in the API's own words (a build already running, somebody else publishing
 * first, an answer the build would not read) and nothing is lost from the form.
 *
 * THE INFORMATION INPUTS (live bug 2026-10-14): the website address, the pasted text and the documents a "Customer
 * support" agent was built from start filled in and count for their required group (`./edit-rules.ts`); the group's
 * sentence is drawn under the fields, as the wizard draws it, and the api's information refusals are said in the
 * portal's language. A file picked for an upload that takes several is kept under its own slot and uploaded with the
 * others; picking the first file lets the kept alternatives of a required group go, and "Keep … instead" puts every
 * kept document back.
 *
 * TYPED TEXT BESIDE A KEPT DOCUMENT (live bug 2026-10-28): the uploads are the wizard's own control, so typing into an
 * upload that may be typed never replaces its kept document by itself — "Replace … with this text" does, and the save
 * area then says which kept document the typed text replaces and how long the text is; "Keep … instead" undoes it.
 *
 * QUESTIONS EDITED ON THE QUESTIONS TAB (plan-030 WAPP-Q, design §41.10): on top of that, when a survey's questions were
 * saved in the survey question editor, Save with a new questionnaire file — picked, or typed and confirmed — first asks
 * "This replaces your edited questions": "Replace them and save" saves, "Keep my questions" puts every kept document
 * back. An Edit that keeps the documents carries the questions as they are (the questionnaire carry), so it asks nothing.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Icon, Textarea } from "@wetalk/ui";
import { useEffect, useState, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { LanguageBetaMark, languageBetaNoteOf } from "../../../shell/language-beta.js";
import { useGateway } from "../../../api/provider.js";
import { QUERY_KEY, agentTemplateQuery, languagePackQuery, voiceQuery } from "../../../api/query.js";
import type { AgentTemplateCardView, LanguagePackView, VoiceView } from "../../../api/gateway.js";
import type { VoiceAgentAnswerView } from "../../../api/slice/voice-agent-edit.js";
import { uploadDocumentsThenStart, progressText, type DocumentUploadProgress } from "../new/document-upload.js";
import type { OpeningWeek } from "../new/opening-hours.js";
import type { WizardAnswer } from "../new/wizard-draft.js";
import {
  PROBLEM_KEY,
  contentFieldOf,
  fileSlotOf,
  fileSlotsOf,
  namedFileOf,
  takesSeveralFiles,
  uploadFileOf,
  inWords,
  languageNameOf,
  orderBookFieldOf,
  resolve,
  speaksOneLanguage,
} from "../new/wizard-rules.js";
import { ContentFields, OrderBookPanel } from "../new/wizard-screen.js";
import { LanguageVoiceRows } from "../new/wizard-form.js";
import { browserSamplePlaybackOf, useVoiceSamplePlayer, type SamplePlaybackOf } from "../new/voice-sample-play.js";
import {
  editAnswerOf,
  editContextOf,
  editProblemOf,
  editRequestOf,
  keptUploadAnswerOf,
  replacedUploadAnswerOf,
  saveActOf,
} from "./edit-rules.js";

import { editorText } from "./questions/questionnaire-copy.js";

import "../new/new-agent.css";

/** What a save did, for the Versions tab to say and to select. */
export type EditOutcome =
  | { readonly kind: "building"; readonly agentVersionId: string; readonly label: string; readonly started: boolean }
  | { readonly kind: "recording"; readonly recordingEnabled: boolean };

export interface EditPanelProps {
  readonly voiceAgentId: string;
  /** The built version to edit from; `null` for the live one. */
  readonly agentVersionId: string | null;
  readonly onClose: () => void;
  readonly onDone: (outcome: EditOutcome) => void;
  /** How a fetched voice sample or greeting is played. A suite hands in its own; the browser's is the default. */
  readonly playbackOf?: SamplePlaybackOf;
  /** plan-030 WAPP-Q: a survey whose questions were saved in the survey question editor. */
  readonly questionsEdited?: boolean;
}

const ANSWER_KEY = ["voice-agent-answer"] as const;

export function EditPanel(props: EditPanelProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const answer = useQuery({
    queryKey: [...ANSWER_KEY, props.voiceAgentId, props.agentVersionId ?? "live"],
    queryFn: () => gateway.getVoiceAgentAnswers(props.voiceAgentId, props.agentVersionId),
    staleTime: 0,
  });
  const agentTemplate = useQuery(agentTemplateQuery(gateway));
  const languagePack = useQuery(languagePackQuery(gateway));
  const voice = useQuery(voiceQuery(gateway));

  if (answer.isError) {
    return (
      <div className="c-va-card c-va-edit">
        <p className="c-note c-note-critical" role="alert">
          {apiErrorText(answer.error, "")}
        </p>
        <div className="c-va-actions">
          <Button variant="secondary" onClick={props.onClose}>
            {portalText("operations", "close_bbfa773e")}</Button>
        </div>
      </div>
    );
  }
  const card = agentTemplate.data?.find((one) => one.code === answer.data?.agentTemplateCode);
  if (answer.data === undefined || languagePack.data === undefined || voice.data === undefined || agentTemplate.data === undefined) {
    return <p className="c-note">{portalText("operations", "reading_the_agent_s_answers_79210808")}</p>;
  }
  if (card === undefined) {
    return (
      <p className="c-note c-note-critical" role="alert">
        {portalText("operations", "this_agent_s_template_is_not_in_the_catalogue_any_more_so_771a99e1")}</p>
    );
  }
  return (
    <EditForm
      key={answer.data.agentVersionId}
      voiceAgentId={props.voiceAgentId}
      view={answer.data}
      card={card}
      languagePack={languagePack.data}
      voice={voice.data}
      onClose={props.onClose}
      onDone={props.onDone}
      playbackOf={props.playbackOf ?? browserSamplePlaybackOf}
      questionsEdited={props.questionsEdited === true}
    />
  );
}

interface EditFormProps {
  readonly voiceAgentId: string;
  readonly view: VoiceAgentAnswerView;
  readonly card: AgentTemplateCardView;
  readonly languagePack: readonly LanguagePackView[];
  readonly voice: readonly VoiceView[];
  readonly onClose: () => void;
  readonly onDone: (outcome: EditOutcome) => void;
  readonly playbackOf: SamplePlaybackOf;
  readonly questionsEdited: boolean;
}

function EditForm({ voiceAgentId, view, card, languagePack, voice, onClose, onDone, playbackOf, questionsEdited }: EditFormProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const player = useVoiceSamplePlayer(playbackOf);
  const queryClient = useQueryClient();
  // Seeded once from the version's answers: `key` on this component remounts it for another version.
  const [answer, setAnswer] = useState<WizardAnswer>(() => editAnswerOf(view, card));
  const [file, setFile] = useState<Readonly<Record<string, File>>>({});
  const [attempted, setAttempted] = useState(false);
  const [upload, setUpload] = useState<Readonly<Record<string, DocumentUploadProgress>>>({});
  const typedLength = useTypedLength(file);
  // plan-030 WAPP-Q: "This replaces your edited questions", asked once before a save with a new questionnaire file.
  const [confirmingReplace, setConfirmingReplace] = useState(false);

  const context = editContextOf({ card, view, answer, file, languagePack, voice });
  const resolved = resolve(context);
  const problem = editProblemOf(context, resolved);
  const shown = attempted ? problem : {};
  const request = editRequestOf(context, resolved, view);
  const act = saveActOf(request, view, card);
  const orderBook = orderBookFieldOf(card);
  const oneLanguage = speaksOneLanguage(card);
  const languageNames = inWords(resolved.languageCode.map((code) => languageNameOf(languagePack, code)));
  // plan-026 (0176): the chips offer every complete pack; the note names the beta ones among them, from the catalogue.
  const betaNote = languageBetaNoteOf(languagePack);
  const change = (next: (current: WizardAnswer) => WizardAnswer): void => {
    setAnswer(next);
  };
  const errorOf = (key: string): { readonly error?: string } => (shown[key] === undefined ? {} : { error: shown[key] });

  const save = useMutation({
    async mutationFn(): Promise<EditOutcome> {
      if (act === "recording") {
        await gateway.setVoiceAgentRecording(voiceAgentId, request.recordingEnabled, view.liveVersionSeq);
        return { kind: "recording", recordingEnabled: request.recordingEnabled };
      }
      const made = await gateway.editVoiceAgent(voiceAgentId, request);
      if (!made.started) {
        const attached = fileSlotsOf(contentFieldOf(card, answer.fieldValue), answer.fieldValue).flatMap((one) => {
          const picked = file[one.slot];
          return picked !== undefined && picked.size > 0 ? [{ fieldCode: one.slot, file: uploadFileOf(one.field, picked) }] : [];
        });
        await uploadDocumentsThenStart(gateway, voiceAgentId, attached, new Set(), (fieldCode, progress) => {
          setUpload((current) => ({ ...current, [fieldCode]: progress }));
        });
      }
      return { kind: "building", agentVersionId: made.agentVersionId, label: made.agentVersionLabel, started: true };
    },
    async onSuccess(outcome) {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      await queryClient.invalidateQueries({ queryKey: ANSWER_KEY });
      onDone(outcome);
    },
  });

  /** "Keep … instead": every kept document back, every picked or typed file gone. */
  const keepDocuments = (): void => {
    setConfirmingReplace(false);
    setFile({});
    change((current) => ({
      ...current,
      fieldValue: { ...current.fieldValue, ...keptUploadAnswerOf(card, view) },
    }));
  };
  // Live bug 2026-10-28: a kept document that typed text replaces is said beside Save, with the text's length.
  const typedReplacement = typedReplacementOf(card, view, file, typedLength);
  const keptDocument = view.document.map((one) => one.filename).join(", ");
  const pickedName = Object.values(file).map((one) => one.name).join(", ");
  const shownField = contentFieldOf(card, answer.fieldValue);
  // A new questionnaire file, picked or typed, replaces the questions edited on the Questions tab.
  const replacesEditedQuestions = questionsEdited && pickedName !== "";
  const group = [...new Set(shownField.flatMap((field) => (field.requiredGroup === undefined ? [] : [field.requiredGroup])))];
  const saveLabel = save.isPending
    ? portalText("operations", "saving_56a2285c")
    : act === "recording"
      ? request.recordingEnabled
        ? portalText("operations", "turn_recording_on_now_bddbcf0e")
        : portalText("operations", "turn_recording_off_now_cbdd0c64")
      : act === "none"
        ? portalText("operations", "nothing_changed_yet_fab813a5")
        : portalText("operations", "save_and_build_a_new_version_2b24c9c8");

  return (
    <div className="c-va-card c-va-edit">
      <div className="c-va-version-detail-head">
        <p className="c-va-card-title">{portalText("operations", "edit_the_agent_from_6353bec9")}{view.agentVersionLabel}</p>
        <Button variant="ghost" onClick={onClose}>
          {portalText("operations", "cancel_77dfd213")}</Button>
      </div>
      <p className="c-va-card-dek">
        {portalText("operations", "your_changes_build_a_new_version_the_version_answering_now_716766d5")}</p>

      {view.editRefusal === null ? null : (
        <p className="c-va-alert c-va-alert-critical" role="status">
          <span className="c-va-alert-icon">
            <Icon glyph={GLYPH.warningCircle} size="lg" />
          </span>
          <span>{view.editRefusal}</span>
        </p>
      )}

      <div className="c-setup-form">
        {orderBook === undefined ? null : (
          <OrderBookPanel
            label={orderBook.label}
            paymentMethod={answer.paymentMethod}
            paymentError={shown[PROBLEM_KEY.payment]}
            onPayment={(method) => {
              change((current) => ({ ...current, paymentMethod: method }));
            }}
          />
        )}

        {view.document.length === 0 ? null : (
          <p className="c-setup-note">
            {pickedName === ""
              ? portalText("operations", "the_new_version_is_built_from_the_same_document_value_drop_7eedb51f", { v0: String(keptDocument) })
              : portalText("operations", "the_new_version_is_built_from_value_91610099", { v0: String(pickedName) })}
            {pickedName === "" ? null : (
              <Button variant="ghost" onClick={keepDocuments}>
                {portalText("operations", "keep_539b2532")}{keptDocument} {portalText("operations", "instead_571d5bc7")}</Button>
            )}
          </p>
        )}

        {view.unrecoveredField.length === 0 ? null : (
          <p className="c-setup-note">
            {portalText("operations", "wetalk_did_not_keep_c56f8a62")}{" "}
            {inWords(
              view.unrecoveredField.map((code) => card.fieldSet.find((one) => one.code === code)?.label ?? code),
            )}{" "}
            {portalText("operations", "from_the_first_build_fill_7e6ca772")}{view.unrecoveredField.length === 1 ? portalText("operations", "it_6c5522ca") : portalText("operations", "them_98910a6e")} {portalText("operations", "in_again_below_9673edad")}</p>
        )}

        <ContentFields
          fields={shownField}
          context={context}
          shown={shown}
          onValue={(code, value) => {
            change((current) => ({ ...current, fieldValue: { ...current.fieldValue, [code]: value } }));
          }}
          onFile={(code, picked) => {
            const field = card.fieldSet.find((one) => one.code === code);
            const several = field !== undefined && takesSeveralFiles(field);
            const first = Object.keys(file).length === 0;
            setFile((current) => ({ ...current, [field === undefined ? code : fileSlotOf(field, picked.name)]: picked }));
            change((current) => {
              const base = first ? replacedUploadAnswerOf(card, view, current.fieldValue, code) : current.fieldValue;
              const named = several ? [...namedFileOf(base[code]).filter((name) => name !== picked.name), picked.name].join("\n") : picked.name;
              return { ...current, fieldValue: { ...base, [code]: named } };
            });
          }}
          onWeek={(code, week: OpeningWeek) => {
            change((current) => ({ ...current, openingWeek: { ...current.openingWeek, [code]: week } }));
          }}
          onKeep={keepDocuments}
        />

        {group.map((one) =>
          shown[requiredGroupKeyOf(one)] === undefined ? null : (
            <p key={one} className="c-setup-group-error" role="alert">
              {shown[requiredGroupKeyOf(one)]}
            </p>
          ),
        )}

        <Field
          label={oneLanguage ? portalText("operations", "language_89b86ab0") : portalText("operations", "languages_value_selected_5c23e44e", { v0: String(String(resolved.languageCode.length)) })}
          {...(oneLanguage ? { description: portalText("operations", "value_agents_speak_one_language_for_now_91b5111a", { v0: String(card.name) }) } : {})}
          {...errorOf(PROBLEM_KEY.language)}
        >
          {(control) => (
            <>
              <div className="c-setup-chips" id={control.id} role="group" aria-describedby={control["aria-describedby"]}>
                {languagePack.map((pack) => {
                  const chosen = resolved.languageCode.includes(pack.code);
                  return (
                    <button
                      type="button"
                      key={pack.code}
                      className={chosen ? "tag tag-accent c-setup-chip" : "tag tag-neutral c-setup-chip"}
                      aria-pressed={chosen}
                      onClick={() => {
                        const next = oneLanguage
                          ? [pack.code]
                          : chosen
                            ? resolved.languageCode.filter((code) => code !== pack.code)
                            : [...resolved.languageCode, pack.code];
                        change((current) => ({ ...current, languageCode: next }));
                      }}
                    >
                      {pack.name}
                      <LanguageBetaMark pack={languagePack} code={pack.code} />
                    </button>
                  );
                })}
              </div>
              {betaNote === null ? null : <span className="c-setup-hint">{betaNote}</span>}
            </>
          )}
        </Field>

        <Field
          label={portalText("operations", "greeting_9c983d4e")}
          description={portalText("operations", "what_callers_hear_firstvalue_3203e7dd", { v0: String(languageNames === "" ? "" : portalText("operations", "in_value_304fae73", { v0: String(languageNameOf(languagePack, resolved.languageCode[0] ?? "")) })) })}
          {...errorOf(PROBLEM_KEY.greeting)}
        >
          {(control) => (
            <Textarea
              {...control}
              name="greeting"
              value={answer.greeting}
              onChange={(event) => {
                const next = event.currentTarget.value;
                change((current) => ({ ...current, greeting: next }));
              }}
            />
          )}
        </Field>

        {/* Agent setup's order: the languages, the greeting, then a voice for each language. */}
        <LanguageVoiceRows
          row={resolved.voiceRow}
          greeting={resolved.greeting}
          player={player}
          error={shown[PROBLEM_KEY.voice]}
          onVoice={(languageCode, voiceId) => {
            change((current) => ({ ...current, voiceByLanguage: { ...(current.voiceByLanguage ?? {}), [languageCode]: voiceId } }));
          }}
        />

        <Field
          label={portalText("operations", "recording_9b98e1a0")}
          description={portalText("operations", "when_on_callers_hear_a_short_notice_after_the_greeting_and_344b73a4")}
        >
          {(control) => (
            <label className="c-setup-check">
              <input
                {...control}
                type="checkbox"
                checked={answer.recordingEnabled}
                onChange={(event) => {
                  const next = event.currentTarget.checked;
                  change((current) => ({ ...current, recordingEnabled: next }));
                }}
              />
              <span>{portalText("operations", "record_calls_37ca242a")}</span>
            </label>
          )}
        </Field>

        {Object.entries(upload).map(([fieldCode, progress]) => (
          <p key={fieldCode} className="c-setup-note" role="status">
            {progressText(file[fieldCode]?.name ?? fieldCode, progress)}
          </p>
        ))}

        {typedReplacement.map((one) => (
          <div key={one.fieldCode} className="c-setup-replace">
            <p className="c-setup-replace-text" role="status">
              <Icon glyph={GLYPH.warningCircle} size="md" />
              <span>{portalText("operations", "edit.typed_replace.save_notice", { label: one.label, file: one.keptName, count: one.length })}</span>
            </p>
          </div>
        ))}

        {confirmingReplace && replacesEditedQuestions ? (
          <div className="c-setup-replace" role="alertdialog" aria-labelledby="c-edit-replace-title">
            <p className="c-setup-replace-text" id="c-edit-replace-title">
              <Icon glyph={GLYPH.warningCircle} size="md" />
              <strong>{editorText("edit.replace_title")}</strong>
            </p>
            <p className="c-setup-replace-text">{editorText("edit.replace_dek", { file: pickedName })}</p>
            <div className="c-setup-actions">
              <Button
                variant="primary"
                disabled={save.isPending}
                onClick={() => {
                  setConfirmingReplace(false);
                  save.mutate();
                }}
              >
                {editorText("edit.replace_confirm")}
              </Button>
              <Button variant="secondary" disabled={save.isPending} onClick={keepDocuments}>
                {editorText("edit.replace_keep")}
              </Button>
            </div>
          </div>
        ) : null}

        <div className="c-setup-actions">
          <Button
            variant="primary"
            disabled={save.isPending || act === "none" || view.editRefusal !== null || confirmingReplace}
            onClick={() => {
              setAttempted(true);
              if (Object.keys(problem).length > 0) return;
              if (replacesEditedQuestions) {
                setConfirmingReplace(true);
                return;
              }
              save.mutate();
            }}
          >
            <Icon glyph={act === "recording" ? GLYPH.checkCircle : GLYPH.gitBranch} size="md" />
            <span>{saveLabel}</span>
          </Button>
          <Button variant="secondary" disabled={save.isPending} onClick={onClose}>
            {portalText("operations", "cancel_77dfd213")}</Button>
        </div>
        {attempted && Object.keys(problem).length > 0 ? (
          <p className="c-note c-note-critical" role="status">
            {Object.keys(problem).length === 1
              ? portalText("operations", "one_answer_above_needs_attention_before_you_save_76aa76bf")
              : portalText("operations", "value_answers_above_need_attention_before_you_save_0c759628", { v0: String(String(Object.keys(problem).length)) })}
          </p>
        ) : null}
        {save.isError ? (
          <p className="c-note c-note-critical" role="alert">
            {wizardErrorText(save.error, card.fieldSet, answer.fieldValue)}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Live bug 2026-10-28: a kept document of an upload that may be typed, replaced by the text the person typed. */
interface TypedReplacement {
  readonly fieldCode: string;
  readonly label: string;
  readonly keptName: string;
  /** The typed text's length, in characters. */
  readonly length: number;
}

/**
 * The kept documents typed text replaces on Save: an upload that may be typed (`allowText`) whose file is the box's
 * (`<code>.txt`) while the version kept a document for it.
 */
function typedReplacementOf(
  card: AgentTemplateCardView,
  view: VoiceAgentAnswerView,
  file: Readonly<Record<string, File>>,
  typedLength: Readonly<Record<string, number>>,
): readonly TypedReplacement[] {
  const kept = keptUploadAnswerOf(card, view);
  return card.fieldSet.flatMap((field) => {
    if (field.kind !== "upload" || field.allowText !== true) return [];
    const keptName = kept[field.code];
    const length = typedLength[field.code];
    if (keptName === undefined || keptName === "" || file[field.code]?.name !== `${field.code}.txt` || length === undefined) return [];
    return [{ fieldCode: field.code, label: field.label, keptName, length }];
  });
}

/** The length in characters of every typed file (`<slot>.txt`) the form holds, by its slot — read again as each changes. */
function useTypedLength(file: Readonly<Record<string, File>>): Readonly<Record<string, number>> {
  const [length, setLength] = useState<Readonly<Record<string, number>>>({});
  useEffect(() => {
    let active = true;
    const typed = Object.entries(file).filter(([slot, one]) => one.name === `${slot}.txt`);
    void Promise.all(typed.map(async ([slot, one]) => [slot, [...(await one.text())].length] as const)).then((read) => {
      if (active) setLength(Object.fromEntries(read));
    });
    return () => {
      active = false;
    };
  }, [file]);
  return length;
}
